import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { NAV } from "@/constants/testIds";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_5f66e06f-babb-477d-81df-046e9cca7dda/artifacts/h62m3jh2_bush%20maps%20logo.png";

// Section anchors live on Home
const ANCHOR_LINKS = [
  { id: "what-is", label: "What is it" },
  { id: "process", label: "Process" },
  { id: "examples", label: "Example Maps" },
  { id: "outputs", label: "Outputs" },
  { id: "contact", label: "Contact" },
];

// Dedicated pages
const PAGE_LINKS = [{ to: "/approach", label: "Approach" }];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isHome = pathname === "/";

  // On non-Home pages the nav must always be solid (no transparent over hero)
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToAnchor = (id) => {
    setOpen(false);
    if (isHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <header
      data-testid={NAV.container}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-[#FDFBF7]/95 backdrop-blur border-b border-[#E2DACF]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex items-center gap-3 group"
        >
          <span
            className={`w-10 h-10 rounded-full overflow-hidden border ${
              solid ? "border-[#C5B6A3]" : "border-white/50"
            }`}
          >
            <img
              src={LOGO_URL}
              alt="Bush Maps"
              className="w-full h-full object-cover"
            />
          </span>
          <span
            className={`font-serif-display text-xl tracking-tight ${
              solid ? "text-[#2D2621]" : "text-white"
            }`}
          >
            Bush Maps
          </span>
        </Link>

        {/* Desktop links */}
        <nav className="hidden md:flex items-center gap-7">
          {ANCHOR_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={NAV.link(l.id)}
              onClick={() => goToAnchor(l.id)}
              className={`text-sm tracking-wide transition-colors ${
                solid
                  ? "text-[#584D45] hover:text-[#A95A3F]"
                  : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
            </button>
          ))}
          {PAGE_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-page-${l.to.replace("/", "")}`}
              className={`text-sm tracking-wide transition-colors ${
                solid
                  ? "text-[#584D45] hover:text-[#A95A3F]"
                  : "text-white/90 hover:text-white"
              } ${pathname === l.to ? "!text-[#A95A3F]" : ""}`}
            >
              {l.label}
            </Link>
          ))}
          <button
            onClick={() => goToAnchor("contact")}
            data-testid={NAV.cta}
            className="bm-btn-primary"
          >
            Start a Project
          </button>
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid={NAV.mobileToggle}
          aria-label="Menu"
        >
          {open ? (
            <X className={solid ? "text-[#2D2621]" : "text-white"} />
          ) : (
            <Menu className={solid ? "text-[#2D2621]" : "text-white"} />
          )}
        </button>
      </div>

      {/* Mobile sheet */}
      {open && (
        <div className="md:hidden bg-[#FDFBF7] border-t border-[#E2DACF]">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {ANCHOR_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => goToAnchor(l.id)}
                className="text-left text-[#2D2621] py-2 border-b border-[#E2DACF]"
              >
                {l.label}
              </button>
            ))}
            {PAGE_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-left text-[#2D2621] py-2 border-b border-[#E2DACF]"
              >
                {l.label}
              </Link>
            ))}
            <button
              onClick={() => goToAnchor("contact")}
              className="bm-btn-primary justify-center"
            >
              Start a Project
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
